import { createFileRoute } from "@tanstack/react-router";
import { useDismissed } from "@/hooks/use-dismissed";



const SITE_URL = "https://flaneriet.online";

export const Route = createFileRoute("/om")({
  head: () => ({
    meta: [
      { title: "Om Flâneriet – et samlingspunkt for langsomt liv" },
      {
        name: "description",
        content:
          "Flâneriet er et personligt, kurateret samlingspunkt for vin, kunst, jazz, poesi, litteratur, mad, cykling og levekunst. Læs manifestet og mød redaktøren bag.",
      },
      { property: "og:type", content: "article" },
      { property: "og:site_name", content: "Flâneriet" },
      { property: "og:title", content: "Om Flâneriet" },
      {
        property: "og:description",
        content:
          "Manifestet bag Flâneriet – hvorfor jeg læser langsomt, hvorfor jeg samler, og hvem der står bag.",
      },
      { property: "og:url", content: `${SITE_URL}/om` },
      { property: "og:image", content: `${SITE_URL}/og/og-om.png` },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Flâneriet – Om Flâneriet" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Om Flâneriet" },
      { name: "twitter:description", content: "Manifestet bag Flâneriet – hvorfor jeg læser langsomt, hvorfor jeg samler, og hvem der står bag." },
      { name: "twitter:image", content: `${SITE_URL}/og/og-om.png` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/om` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "Om Flâneriet",
          url: `${SITE_URL}/om`,
          inLanguage: "da-DK",
          publisher: {
            "@type": "Organization",
            name: "Flâneriet",
            url: SITE_URL,
          },
          author: {
            "@type": "Person",
            name: "Mads Poulsen",
          },
        }),
      },
    ],
  }),
  component: OmPage,
});

function OmPage() {
  return (
    <article className="mx-auto max-w-3xl">
      <header className="pt-14 pb-10 text-center border-b border-rule">
        <p className="eyebrow">Manifest</p>
        <h1 className="mt-3 font-serif text-[2.6rem] md:text-[3.6rem] leading-[1.04] text-ink">
          Om Flâneriet
        </h1>
        <p className="mt-4 meta italic max-w-xl mx-auto">
          At drive uden mål gennem byen, bogen, glasset – som opmærksomhed, ikke som flugt.
        </p>
      </header>

      <section className="py-14 font-serif text-[1.15rem] md:text-[1.25rem] leading-[1.8] text-ink space-y-7">
        <p>
          <em>Flâneur</em> – det franske ord for én, der spadserer uden mål; iagttageren, der lader sig
          drive af nysgerrighed frem for hastværk. Baudelaire skrev om ham i det nittende århundrede,
          Walter Benjamin skrev videre i det tyvende. Flâneriet er hans landsted: et sted at komme
          hen, når man vil læse langsomt, se ordentligt og drikke sit glas uden at have travlt.
        </p>

        <p>
          Her samles, dagligt, det bedste inden for naturvin, jazz og klassisk musik,
          kunst, poesi og litteratur, god mad, cykling som livsstil, samt stoicisme og levekunst.
          Det gør en AI, jeg har lært op i min smag. Ingen uendelig scroll. Ingen ambition om at være mest.
          Kun ambitionen om at være rigtig – og at føre videre til den oprindelige kilde, altid.
        </p>

        <p>
          Flâneriet tror på, at det gode liv ikke findes ved højere hastighed, men ved længere
          opmærksomhed. På at et glas vin, en linje af Inger Christensen, en sætning af Rilke eller
          en tur på cykel gennem en efterårsby kan være hele dage værd, hvis man er der. På at
          kedsomheden ikke er fjenden – men netop dét sted, tanken begynder.
        </p>

        <p>
          Sitet er ikke et magasin, ikke en avis, ikke en app. Det er et samlingspunkt. En hylde
          i din reol. Et brev, der kommer regelmæssigt. Et sted at vende tilbage til, når verden
          skynder sig for meget.
        </p>
      </section>

      <OmUdvaelgelsen />



      <section className="py-10 border-t border-rule text-center">
        <p className="eyebrow">Kolofon</p>
        <p className="mt-5 font-serif text-[1.05rem] md:text-[1.15rem] leading-[1.7] italic text-ink-soft max-w-xl mx-auto">
          Af Mads Poulsen.
        </p>
        <p className="mt-4 font-serif text-[1rem] md:text-[1.05rem] leading-[1.7] italic text-ink-soft max-w-xl mx-auto">
          Til minde om Svend Aage Poulsen, Leif Vogensen &amp; Poul Frost-Thorsen. Og bestræbelsen
          på det eftertænksomme, gode liv.
        </p>
      </section>
    </article>
  );
}

function OmUdvaelgelsen() {
  const { ready, dismissed, restore } = useDismissed("flaneriet:how-to-read-dismissed");
  return (
    <section id="om-udvaelgelsen" className="py-12 border-t border-rule scroll-mt-20">
      <p className="eyebrow text-center">Om udvælgelsen</p>
      <div className="mt-6 font-serif text-[1.05rem] md:text-[1.15rem] leading-[1.8] text-ink space-y-6 max-w-2xl mx-auto">
        <p>
          Kort fortalt: jeg har bygget en AI, der hver dag finder de artikler, hvor
          sandsynligheden for at jeg tænker <em>kæft en spændende artikel</em> er så høj
          som mulig. Flâneriet er dét, den lægger frem.
        </p>
        <p>
          Jeg har instrueret den i skrivestil, værdier og emner – naturvin, jazz og
          klassisk musik, kunst, poesi og litteratur, mad, cykling, stoicisme og levekunst.
          Den samme øvelse gælder begivenheder og video: skal jeg have et glas vin og se
          noget lørdag aften, har den allerede fundet et par gode bud, frem for at jeg
          havner i YouTubes rabbit hole.
        </p>
        <p>
          Pointen er, at jeg kan komme uden om sociale medier og endnu et nyhedsbrev og
          alligevel få indhold, der interesserer mig. Der er ingen faste kilder, så det
          vil for det meste være noget, jeg ikke selv havde fundet – og som peger videre
          til den oprindelige udgivelse, altid.
        </p>
        <p>
          Forskellen ligger i smagen, ikke i teknologien. En hylde bygges stadig af den,
          der ved, hvad der skal stå på den.
        </p>
      </div>
      {ready && dismissed && (
        <p className="mt-8 text-center">
          <button
            type="button"
            onClick={restore}
            className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-muted hover:text-ink transition-colors"
          >
            Vis "Sådan læses" på forsiden igen
          </button>
        </p>
      )}
    </section>
  );
}
